import React, { useState, useEffect, useCallback } from "react";
import { Shield, Save, Loader2, Users, Info, Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../foundation/altzero_base/base/components/ui/card";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/alert-dialog";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

type PermissionRow = {
  permission_key: string;
  granted: boolean;
};

type RolePermissions = Record<string, boolean>;

// All permission keys with their labels, descriptions, and categories — purely in code
const PERMISSION_DESCRIPTIONS: Record<string, { label: string; description: string; category: string }> = {
  "tender.create": { label: "Create Tenders", description: "Create new tender proposals", category: "Tenders" },
  "tender.view": { label: "View Tenders", description: "View tender details and steps", category: "Tenders" },
  "tender.edit": { label: "Edit Tenders", description: "Edit tender details, client info, offer data", category: "Tenders" },
  "tender.delete": { label: "Delete Tenders", description: "Permanently delete tenders", category: "Tenders" },
  "tender.send_proposal": { label: "Send Proposals", description: "Send the final proposal to client", category: "Tenders" },
  "client.create": { label: "Create Clients", description: "Create a new client record", category: "Clients" },
  "client.edit": { label: "Edit Clients", description: "Edit client details", category: "Clients" },
  "client.delete": { label: "Delete Clients", description: "Delete a client record", category: "Clients" },
  // Contacts have their own table and page; until now none of its actions asked for a
  // permission, so every role could delete one. See lib/contactPermissions.ts.
  "contact.create": { label: "Create Contacts", description: "Add a contact to a client", category: "Contacts" },
  "contact.edit":   { label: "Edit Contacts", description: "Change an existing contact's details", category: "Contacts" },
  "contact.delete": { label: "Delete Contacts", description: "Permanently delete a contact record. Separate from editing, so a role can keep contacts current without being able to remove them.", category: "Contacts" },
  // Sites and Retailers had no gating at all — every role could delete either.
  // See lib/resourcePermissions.ts.
  "site.create":     { label: "Create Sites", description: "Add a site to the Resource Library", category: "Sites" },
  "site.edit":       { label: "Edit Sites", description: "Change a site's details", category: "Sites" },
  "site.delete":     { label: "Delete Sites", description: "Permanently delete a site record", category: "Sites" },
  "retailer.create": { label: "Create Retailers", description: "Add a retailer or retailer group", category: "Retailers" },
  "retailer.edit":   { label: "Edit Retailers", description: "Change a retailer or group's details", category: "Retailers" },
  "retailer.delete": { label: "Delete Retailers", description: "Permanently delete a retailer or group", category: "Retailers" },
  "editor.view": { label: "View Only", description: "Preview the editor and export/download PDF, DOCX, HTML. No editing allowed.", category: "Editor" },
  "editor.layout": { label: "Layout Control", description: "Align pages, hide/show pages, reorder pages, switch page views. Cannot edit block content.", category: "Editor" },
  "editor.content": { label: "Content Editing", description: "Edit block text, styling, and properties. Copy/paste blocks. Cannot add or delete blocks/pages.", category: "Editor" },
  "editor.full": { label: "Full Control", description: "Full editor access — add/delete blocks and pages, AI regeneration, page backgrounds, global typography.", category: "Editor" },
  "bill_validation.view": { label: "View Bill Validation", description: "Access the Bill Validation module and see it in the navigation", category: "Bill Validation" },

  /*
   * One key per wizard step. Ticking a subset is how a role is limited to part of the flow —
   * for example Compare & Select plus Generate, and nothing in between.
   *
   * ⚠️ These gate the UI: the stepper hides what a role cannot open, and navigating straight to
   * ?step=N redirects to the first step it can. They do not block a crafted API call.
   */
  "tender.step.client_info":      { label: "Step 1 — Client Info", description: "Open the client details and sites step", category: "Tender Steps" },
  "tender.step.retailer_comms":   { label: "Step 2 — Retailer Communication", description: "Open the retailer email step", category: "Tender Steps" },
  "tender.step.email_processing": { label: "Step 3 — Email Processing", description: "Open the retailer reply processing step", category: "Tender Steps" },
  "tender.step.offers":           { label: "Step 4 — Offers Entry", description: "Open the offer entry step", category: "Tender Steps" },
  "tender.step.compare_select":   { label: "Step 5 — Compare & Select", description: "Open the offer comparison and ranking step", category: "Tender Steps" },
  // `tender.step.recommended` is deliberately absent — the Recommended Offer step was removed.
  // Rows for it survive in org_role_permissions and are inert; listing it would offer an admin a
  // switch that turns nothing on.
  "tender.step.template":         { label: "Step 6 — Template Selection", description: "Open the proposal template step", category: "Tender Steps" },
  "tender.step.offer_details":    { label: "Step 7 — Offer Details", description: "Open the offer detail settings step", category: "Tender Steps" },
  "tender.step.generate":         { label: "Step 8 — Generate Proposal", description: "Open the proposal generation step", category: "Tender Steps" },
  // Not a step key: opening Offer Details and rewriting the figures it shows are different rights.
  // See lib/costEditPermission.ts.
  "tender.costs.edit":            { label: "Edit Cost Figures", description: "Change the energy, environmental, network and other costs on Offer Details, and re-run the calculation. Without it the step is read-only.", category: "Tender Steps" },
};

const ALL_PERMISSION_KEYS = Object.keys(PERMISSION_DESCRIPTIONS);
const CATEGORIES = [...new Set(Object.values(PERMISSION_DESCRIPTIONS).map((d) => d.category))];

const formatRoleName = (name: string) => name.replace(/_/g, " ");

export const SettingsPermissionsTab: React.FC = () => {
  const { isAdmin, orgId } = usePermissions();
  const { toast } = useToast();
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [permissions, setPermissions] = useState<RolePermissions>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Create role state
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Rename role state
  const [renamingRole, setRenamingRole] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete role state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load distinct non-admin roles from the org_role_permissions table
  const loadRoles = useCallback(async () => {
    if (!orgId) return;

    try {
      const { data, error } = await supabase
        .from("org_role_permissions")
        .select("role")
        .eq("org_id", orgId);

      if (error) throw error;

      const roles = [...new Set((data || []).map((r) => r.role))];
      setAvailableRoles(roles);

      // Auto-select first role if none selected or selected was deleted
      if ((!selectedRole || !roles.includes(selectedRole)) && roles.length > 0) {
        setSelectedRole(roles[0]);
      }
      if (roles.length === 0) {
        setSelectedRole("");
      }
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  }, [orgId, selectedRole]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const loadPermissions = useCallback(async () => {
    if (!orgId || !selectedRole) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("org_role_permissions")
        .select("permission_key, granted")
        .eq("org_id", orgId)
        .eq("role", selectedRole);

      if (error) throw error;

      const permMap: RolePermissions = {};
      (data || []).forEach((row: PermissionRow) => {
        permMap[row.permission_key] = row.granted;
      });
      setPermissions(permMap);
      setHasChanges(false);
    } catch (error) {
      console.error("Error loading permissions:", error);
      toast({
        title: "Error",
        description: "Failed to load permissions",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [orgId, selectedRole, toast]);

  useEffect(() => {
    if (selectedRole) {
      loadPermissions();
    }
  }, [loadPermissions, selectedRole]);

  const handleToggle = (key: string) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!orgId || !selectedRole) return;

    try {
      setIsSaving(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const permArray = Object.entries(permissions).map(([key, granted]) => ({
        org_id: orgId,
        role: selectedRole,
        permission_key: key,
        granted,
        updated_at: new Date().toISOString(),
        updated_by: user.id,
      }));

      const { error } = await supabase
        .from("org_role_permissions")
        .upsert(permArray, { onConflict: "org_id,role,permission_key" });

      if (error) throw error;

      toast({
        title: "Saved",
        description: `Permissions for "${formatRoleName(selectedRole)}" role updated successfully.`,
      });
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast({
        title: "Error",
        description: "Failed to save permissions",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!orgId || !newRoleName.trim()) return;

    const sanitized = newRoleName.trim().toLowerCase().replace(/\s+/g, "_");

    if (sanitized === "admin") {
      toast({ title: "Error", description: "Cannot create a role named 'admin'", variant: "destructive" });
      return;
    }

    if (availableRoles.includes(sanitized)) {
      toast({ title: "Error", description: `Role "${formatRoleName(sanitized)}" already exists`, variant: "destructive" });
      return;
    }

    try {
      setIsCreating(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Insert all permission keys with granted=false
      const rows = ALL_PERMISSION_KEYS.map((key) => ({
        org_id: orgId,
        role: sanitized,
        permission_key: key,
        granted: false,
        updated_at: new Date().toISOString(),
        updated_by: user.id,
      }));

      const { error } = await supabase.from("org_role_permissions").insert(rows);
      if (error) throw error;

      toast({
        title: "Role Created",
        description: `"${formatRoleName(sanitized)}" role has been created. Configure its permissions below.`,
      });

      setNewRoleName("");
      setShowCreateInput(false);
      setSelectedRole(sanitized);
      await loadRoles();
    } catch (error) {
      console.error("Error creating role:", error);
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to create role",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRenameRole = async () => {
    if (!orgId || !renamingRole || !renameValue.trim()) return;

    const sanitized = renameValue.trim().toLowerCase().replace(/\s+/g, "_");

    if (sanitized === "admin") {
      toast({ title: "Error", description: "Cannot rename to 'admin'", variant: "destructive" });
      return;
    }

    if (sanitized === renamingRole) {
      setRenamingRole(null);
      return;
    }

    if (availableRoles.includes(sanitized)) {
      toast({ title: "Error", description: `Role "${formatRoleName(sanitized)}" already exists`, variant: "destructive" });
      return;
    }

    try {
      setIsRenaming(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update org_role_permissions rows
      const { error: permError } = await supabase
        .from("org_role_permissions")
        .update({ role: sanitized, updated_at: new Date().toISOString(), updated_by: user.id })
        .eq("org_id", orgId)
        .eq("role", renamingRole);

      if (permError) throw permError;

      // Update any members who have this role
      const { error: memberError } = await supabase
        .from("base_organisation_members")
        .update({ role: sanitized })
        .eq("organisation_id", orgId)
        .eq("role", renamingRole);

      if (memberError) throw memberError;

      toast({
        title: "Role Renamed",
        description: `"${formatRoleName(renamingRole)}" renamed to "${formatRoleName(sanitized)}".`,
      });

      if (selectedRole === renamingRole) {
        setSelectedRole(sanitized);
      }
      setRenamingRole(null);
      setRenameValue("");
      await loadRoles();
    } catch (error) {
      console.error("Error renaming role:", error);
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to rename role",
        variant: "destructive",
      });
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!orgId || !roleToDelete) return;

    // Pick a fallback role: first available role that isn't the one being deleted, or "member"
    const fallback = availableRoles.find((r) => r !== roleToDelete) || "member";

    try {
      setIsDeleting(true);

      // Delete permission rows
      const { error: permError } = await supabase
        .from("org_role_permissions")
        .delete()
        .eq("org_id", orgId)
        .eq("role", roleToDelete);

      if (permError) throw permError;

      // Reassign members to fallback
      const { error: memberError } = await supabase
        .from("base_organisation_members")
        .update({ role: fallback })
        .eq("organisation_id", orgId)
        .eq("role", roleToDelete);

      if (memberError) throw memberError;

      toast({
        title: "Role Deleted",
        description: `"${formatRoleName(roleToDelete)}" has been deleted. Affected members reassigned to "${formatRoleName(fallback)}".`,
      });

      if (selectedRole === roleToDelete) {
        setSelectedRole("");
      }
      setRoleToDelete(null);
      setDeleteDialogOpen(false);
      await loadRoles();
    } catch (error) {
      console.error("Error deleting role:", error);
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to delete role",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p className="text-lg font-medium">Access Denied</p>
        <p className="text-sm mt-1">Only organization admins can manage permissions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Role Permissions
          </h2>
          <p className="text-gray-500 mt-1">
            Create, rename, delete roles and configure what each role can do.
          </p>
        </div>
        {hasChanges && (
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        )}
      </div>

      {/* Role selector + management */}
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          {availableRoles.map((r) => (
            <div key={r} className="flex items-center gap-1">
              {renamingRole === r ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="h-9 w-40"
                    placeholder="New role name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRenameRole();
                      if (e.key === "Escape") setRenamingRole(null);
                    }}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleRenameRole}
                    disabled={isRenaming}
                    className="h-9 w-9 p-0"
                  >
                    {isRenaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRenamingRole(null)}
                    className="h-9 w-9 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center">
                  <Button
                    variant={selectedRole === r ? "default" : "outline"}
                    onClick={() => setSelectedRole(r)}
                    className="gap-2 capitalize"
                  >
                    <Users className="w-4 h-4" />
                    {formatRoleName(r)}
                  </Button>
                  {selectedRole === r && (
                    <div className="flex ml-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setRenamingRole(r);
                          setRenameValue(formatRoleName(r));
                        }}
                        className="h-8 w-8 p-0"
                        title="Rename role"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setRoleToDelete(r);
                          setDeleteDialogOpen(true);
                        }}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        title="Delete role"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Create role button / input */}
          {showCreateInput ? (
            <div className="flex items-center gap-1">
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                className="h-9 w-40"
                placeholder="Role name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateRole();
                  if (e.key === "Escape") {
                    setShowCreateInput(false);
                    setNewRoleName("");
                  }
                }}
                autoFocus
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCreateRole}
                disabled={isCreating || !newRoleName.trim()}
                className="h-9 w-9 p-0"
              >
                {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowCreateInput(false);
                  setNewRoleName("");
                }}
                className="h-9 w-9 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowCreateInput(true)}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Role
            </Button>
          )}
        </div>

        {availableRoles.length === 0 && !isLoading && !showCreateInput && (
          <div className="text-sm text-gray-500">
            No roles configured yet. Click "New Role" to create one.
          </div>
        )}
      </div>

      {/* Info banner */}
      {/* <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-700">
          Toggle individual permissions on or off for the selected role. Changes apply to
          all users with this role in your organization.
          <div className="mt-1 text-blue-600">
            <strong>Admin</strong> role always has full access and is not shown here.
          </div>
        </div>
      </div> */}

      {/* Permissions grid */}
      {isLoading && selectedRole ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : selectedRole ? (
        <div className="space-y-6">
          {CATEGORIES.map((category) => {
            const categoryPerms = ALL_PERMISSION_KEYS.filter(
              (key) => PERMISSION_DESCRIPTIONS[key]?.category === category
            );

            if (categoryPerms.length === 0) return null;

            return (
              <Card key={category} className="border border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-gray-800">
                    {category}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {categoryPerms.map((key) => {
                      const info = PERMISSION_DESCRIPTIONS[key];
                      if (!info) return null;
                      const isGranted = permissions[key] ?? false;

                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {info.label}
                            </p>
                            <p className="text-xs text-gray-500">
                              {info.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isGranted}
                            onClick={() => handleToggle(key)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                              isGranted ? "bg-primary" : "bg-gray-200"
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                isGranted ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}

      {/* Delete Role Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{roleToDelete ? formatRoleName(roleToDelete) : ""}" role?
              All members with this role will be reassigned to{" "}
              "{formatRoleName(availableRoles.find((r) => r !== roleToDelete) || "member")}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRole}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete Role"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
